<template>
  <div class="d-flex align-center ga-2">
    <button
      v-if="findings.length"
      type="button"
      class="summary-chip"
      :class="issuesPillClass"
      data-testid="validation-issues-chip"
      @click="showIssues = true"
    >
      <SoIcon icon="alert-circle" size="x-small" />
      <span>{{ issuesLabel }}</span>
    </button>
    <button
      v-if="assets.length"
      type="button"
      class="summary-chip"
      data-testid="validation-assets-chip"
      @click="showAssets = true"
    >
      <SoIcon icon="file-image" size="x-small" />
      <span>{{ assetsLabel }}</span>
    </button>
    <button
      v-if="variables.length"
      type="button"
      class="summary-chip"
      data-testid="validation-variables-chip"
      @click="showVariables = true"
    >
      <SoIcon icon="code" size="x-small" />
      <span>{{ variablesLabel }}</span>
    </button>
    <button
      v-if="links.length"
      type="button"
      class="summary-chip"
      data-testid="validation-links-chip"
      @click="showLinks = true"
    >
      <SoIcon icon="link" size="x-small" />
      <span>{{ linksLabel }}</span>
    </button>

    <SoDialog
      v-model="showIssues"
      card-title="Issues to fix"
      :max-width="640"
      data-testid="validation-issues-dialog"
    >
      <div v-for="group in groupedFindings" :key="group.severity" class="mb-4">
        <div class="text-subtitle-2 mb-2" :class="group.textClass">
          {{ group.label }} ({{ group.findings.length }})
        </div>
        <ul class="finding-list">
          <li
            v-for="(finding, index) in group.findings"
            :key="index"
            class="mb-1"
          >
            <div>{{ finding.message }}</div>
            <code v-if="finding.detail" class="finding-detail">{{
              finding.detail
            }}</code>
          </li>
        </ul>
      </div>
    </SoDialog>

    <SoDialog
      v-model="showAssets"
      card-title="Template assets"
      :max-width="720"
      data-testid="validation-assets-dialog"
    >
      <div
        v-if="checkingAssets"
        class="text-caption text-medium-emphasis mb-3"
        data-testid="assets-loading"
      >
        Checking assets…
      </div>
      <ul v-if="assets.length" class="asset-list">
        <li v-for="asset in assets" :key="asset.url" class="mb-4">
          <a
            v-if="asset.absolute"
            :href="asset.url"
            target="_blank"
            rel="noopener"
            class="text-primary asset-url"
          >
            {{ asset.url }}
          </a>
          <div v-else class="asset-url">{{ asset.url }}</div>
          <div class="d-flex flex-wrap align-center ga-2 mt-1">
            <SoChip
              :type="assetStatusType(asset)"
              :text="assetStatusLabel(asset)"
              size="small"
            />
            <span class="text-caption text-medium-emphasis">
              {{
                asset.width !== null
                  ? `${asset.width}×${asset.height}px`
                  : "Size unknown"
              }}
            </span>
            <SoChip
              v-if="asset.inline"
              :type="SoChipType.WARNING"
              text="Embedded"
              size="small"
            />
            <SoChip
              v-else-if="!asset.absolute"
              :type="SoChipType.ERROR"
              text="Relative URL"
              size="small"
            />
            <SoChip
              v-else-if="!asset.allowed"
              :type="SoChipType.ERROR"
              text="Unapproved host"
              size="small"
            />
          </div>
        </li>
      </ul>
      <div v-else-if="!checkingAssets" class="text-medium-emphasis">
        No assets found in this template.
      </div>
    </SoDialog>

    <SoDialog
      v-model="showVariables"
      card-title="Template variables"
      :max-width="560"
      data-testid="validation-variables-dialog"
    >
      <SoAlert
        type="warning"
        text="Please verify in the backend code that all variables are passed to the template and correctly formatted."
        class="mb-6"
        data-testid="variables-warning"
      />
      <div v-if="variables.length" class="d-flex flex-wrap ga-2">
        <SoChip
          v-for="variable in variables"
          :key="variable"
          :type="SoChipType.IMPORTANT"
          :text="variable"
          size="small"
        />
      </div>
      <div v-else class="text-medium-emphasis">
        No variables found in this template.
      </div>
    </SoDialog>

    <SoDialog
      v-model="showLinks"
      card-title="Template links"
      :max-width="640"
      data-testid="validation-links-dialog"
    >
      <SoAlert
        type="warning"
        text="Please check each link resolves correctly."
        class="mb-6"
        data-testid="links-warning"
      />
      <ul v-if="links.length" class="link-list">
        <li v-for="link in links" :key="link" class="mb-2">
          <a :href="link" target="_blank" rel="noopener" class="text-primary">
            {{ link }}
          </a>
        </li>
      </ul>
      <div v-else class="text-medium-emphasis">
        No links found in this template.
      </div>
    </SoDialog>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoChip from "@/components/SoChip.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import { SoChipType } from "@/types/components/SoChip"
  import {
    AssetCheck,
    ValidationFinding,
    ValidationSeverity,
  } from "@/services/admin/emailTemplateValidation/emailTemplateValidation.types"

  interface FindingGroup {
    severity: ValidationSeverity
    label: string
    textClass: string
    findings: ValidationFinding[]
  }

  const SEVERITY_GROUPS: Omit<FindingGroup, "findings">[] = [
    { severity: "error", label: "Errors", textClass: "text-error" },
    { severity: "warning", label: "Warnings", textClass: "text-caution" },
    { severity: "unverified", label: "Unverified", textClass: "" },
    { severity: "info", label: "Info", textClass: "" },
  ]

  const ASSET_STATUS_LABELS: Record<AssetCheck["status"], string> = {
    available: "Available",
    unreachable: "Unreachable",
    unverified: "Unverified",
  }

  export default defineComponent({
    components: { SoChip, SoDialog, SoAlert, SoIcon },
    props: {
      findings: {
        type: Array as PropType<ValidationFinding[]>,
        default: () => [],
      },
      assets: {
        type: Array as PropType<AssetCheck[]>,
        default: () => [],
      },
      variables: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
      links: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
      checkingAssets: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        SoChipType,
        showIssues: false,
        showAssets: false,
        showVariables: false,
        showLinks: false,
      }
    },
    computed: {
      issuesLabel(): string {
        const count = this.findings.length
        return `${count} issue${count === 1 ? "" : "s"}`
      },
      assetsLabel(): string {
        const count = this.assets.length
        return `${count} asset${count === 1 ? "" : "s"}`
      },
      variablesLabel(): string {
        const count = this.variables.length
        return `${count} variable${count === 1 ? "" : "s"}`
      },
      linksLabel(): string {
        const count = this.links.length
        return `${count} link${count === 1 ? "" : "s"}`
      },
      issuesPillClass(): string {
        return this.findings.some((finding) => finding.severity === "error")
          ? "summary-chip--error"
          : "summary-chip--warning"
      },
      groupedFindings(): FindingGroup[] {
        return SEVERITY_GROUPS.map((group) => ({
          ...group,
          findings: this.findings.filter(
            (finding) => finding.severity === group.severity,
          ),
        })).filter((group) => group.findings.length)
      },
    },
    methods: {
      assetStatusType(asset: AssetCheck): SoChipType {
        if (asset.status === "available") return SoChipType.SUCCESS
        if (asset.status === "unreachable") return SoChipType.ERROR
        return SoChipType.WARNING
      },
      assetStatusLabel(asset: AssetCheck): string {
        return ASSET_STATUS_LABELS[asset.status]
      },
    },
  })
</script>

<style scoped>
  .summary-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    border-radius: 16px;
    border: 1px solid rgb(var(--v-theme-border));
    background: rgb(var(--v-theme-surface));
    color: rgb(var(--v-theme-on-surface));
    font-size: 0.8125rem;
    line-height: 1;
    cursor: pointer;
  }
  .summary-chip :deep(.v-icon) {
    display: block;
  }
  .summary-chip--error {
    color: rgb(var(--v-theme-error));
    border-color: rgb(var(--v-theme-error));
  }
  .summary-chip--warning {
    color: rgb(var(--v-theme-caution));
    border-color: rgb(var(--v-theme-caution));
  }
  .link-list,
  .asset-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  .finding-list {
    list-style: disc;
    padding-left: 1.25rem;
    margin: 0;
  }
  .link-list a,
  .asset-url {
    word-break: break-all;
  }
  .finding-detail {
    display: inline-block;
    word-break: break-all;
    font-size: 0.75rem;
    padding: 2px 6px;
    border-radius: 4px;
    background: rgb(var(--v-theme-surface-dim));
    color: rgb(var(--v-theme-on-surface));
  }
</style>
